import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, MailCheck, MailWarning, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { TwoFactorSetup, BackupCodes, PasswordStrength, codeCls } from "./TwoFactor";
import { inputCls } from "../community/JoinSteps";

const Card = ({ children, testid }) => <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-6 space-y-4" data-testid={testid}>{children}</div>;

export const VerifyBanner = () => {
  const { user } = useAuth();
  const { tr } = useLang();
  const [sent, setSent] = useState(false);
  if (!user || user.email_verified) return null;
  const resend = async () => { try { await api.post("/auth/resend-verification"); setSent(true); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-2xl bg-[#F2C230]/15 border border-[#F2C230]/50 p-4 flex flex-wrap items-center gap-3 text-sm" data-testid="verify-email-banner">
      <MailWarning className="h-5 w-5 text-[#B8860B]" />
      <span className="flex-1">{tr("Bitte bestätige deine E-Mail-Adresse – wir haben dir einen Link geschickt.", "Please confirm your email address – we sent you a link.")}</span>
      {sent ? <span className="text-[#2E9E6B] font-semibold" data-testid="verify-resent">{tr("Neu gesendet ✓", "Sent again ✓")}</span>
        : <button onClick={resend} className="btn-outline" data-testid="resend-verification-btn">{tr("Erneut senden", "Resend")}</button>}
    </div>
  );
};

const ChangePassword = () => {
  const { tr } = useLang();
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", f); setF({ current_password: "", new_password: "" }); toast.success(tr("Passwort geändert", "Password changed")); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      <input type="password" required placeholder={tr("Aktuelles Passwort", "Current password")} value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className={inputCls} data-testid="current-password-input" />
      <input type="password" required minLength={8} placeholder={tr("Neues Passwort", "New password")} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className={inputCls} data-testid="new-password-input" />
      <PasswordStrength pw={f.new_password} />
      <button className="btn-dark" data-testid="change-password-btn">{tr("Passwort ändern", "Change password")}</button>
    </form>
  );
};

const ManageTwoFA = ({ sec, reload }) => {
  const { tr } = useLang();
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const sendCode = () => api.post("/auth/2fa/email-code").then(() => toast.success(tr("Code gesendet", "Code sent"))).catch((e) => toast.error(errMsg(e)));
  const act = async (e) => {
    e.preventDefault();
    try {
      if (mode === "disable") { await api.post("/auth/2fa/disable", { password: pw, code }); toast.success(tr("2FA deaktiviert", "2FA disabled")); setMode(null); reload(); }
      else { const { data } = await api.post("/auth/2fa/backup-codes", { code }); setCodes(data.backup_codes); setMode(null); }
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (codes) return <BackupCodes codes={codes} onDone={() => { setCodes(null); reload(); }} />;
  return (
    <div className="space-y-4">
      <p className="flex items-center gap-2 text-sm"><ShieldCheck className="h-5 w-5 text-[#2E9E6B]" />{tr("Aktiv", "Active")}: <strong data-testid="twofa-active-method">{sec.twofa_method === "totp" ? tr("Authenticator-App", "Authenticator app") : tr("Code per E-Mail", "Email code")}</strong>
        <span className="text-xs text-[#707783]">· {sec.backup_codes_left} {tr("Backup-Codes übrig", "backup codes left")}</span></p>
      {!mode && <div className="flex flex-wrap gap-3">
        <button onClick={() => setMode("codes")} className="btn-outline" data-testid="regen-backup-btn">{tr("Neue Backup-Codes", "New backup codes")}</button>
        {user.role !== "admin" && <button onClick={() => setMode("disable")} className="btn-outline !text-red-600" data-testid="disable-twofa-btn">{tr("2FA deaktivieren", "Disable 2FA")}</button>}
      </div>}
      {mode && (
        <form onSubmit={act} className="space-y-3 max-w-sm" data-testid={`twofa-${mode}-form`}>
          {mode === "disable" && <input type="password" required placeholder={tr("Passwort", "Password")} value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="disable-password-input" />}
          <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder={tr("2FA- oder Backup-Code", "2FA or backup code")} className={codeCls} data-testid="twofa-manage-code-input" />
          {sec.twofa_method === "email" && <button type="button" onClick={sendCode} className="text-xs text-[#6E3FB8] underline" data-testid="send-email-code-btn">{tr("Code per E-Mail senden", "Email me a code")}</button>}
          <div className="flex gap-3"><button type="button" onClick={() => setMode(null)} className="btn-outline">{tr("Abbrechen", "Cancel")}</button><button className="btn-dark" data-testid="twofa-manage-submit">{tr("Bestätigen", "Confirm")}</button></div>
        </form>
      )}
    </div>
  );
};

export const SecurityPanel = () => {
  const { tr } = useLang();
  const { refresh, logout } = useAuth();
  const [sec, setSec] = useState(null);
  const reload = useCallback(() => api.get("/auth/security").then((r) => setSec(r.data)), []);
  useEffect(() => { reload(); }, [reload]);
  if (!sec) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="space-y-6" data-testid="security-panel">
      <h2 className="font-display text-3xl">{tr("Sicherheit", "Security")}</h2>
      <Card testid="security-email"><p className="flex items-center gap-2 text-sm">{sec.email_verified ? <MailCheck className="h-5 w-5 text-[#2E9E6B]" /> : <MailWarning className="h-5 w-5 text-[#B8860B]" />}
        {sec.email}: <strong data-testid="email-verified-status">{sec.email_verified ? tr("bestätigt", "verified") : tr("nicht bestätigt", "not verified")}</strong></p></Card>
      <Card testid="security-twofa">
        <h3 className="font-display text-2xl">{tr("Zwei-Faktor-Authentifizierung", "Two-factor authentication")}</h3>
        {sec.twofa_method ? <ManageTwoFA sec={sec} reload={reload} /> : (<>
          <p className="flex items-center gap-2 text-sm text-[#707783]"><ShieldAlert className="h-5 w-5 text-[#B8860B]" />{tr("Schütze dein Konto mit einem zweiten Faktor – selbst mit deinem Passwort kommt niemand ohne ihn rein.", "Protect your account with a second factor – nobody gets in without it, even with your password.")}</p>
          <TwoFactorSetup onEnabled={async () => { await refresh(); reload(); }} /></>)}
      </Card>
      <Card testid="security-password"><h3 className="font-display text-2xl">{tr("Passwort ändern", "Change password")}</h3><ChangePassword /></Card>
      <Card testid="security-sessions"><h3 className="font-display text-2xl">{tr("Sitzungen", "Sessions")}</h3>
        <button onClick={async () => { await api.post("/auth/logout-all"); await logout().catch(() => {}); window.location.href = "/login"; }} className="btn-outline" data-testid="logout-all-btn"><LogOut className="h-4 w-4" />{tr("Auf allen Geräten abmelden", "Sign out on all devices")}</button></Card>
    </div>
  );
};
